/* v2.2 filtering in the Studio, Power BI style:
 *  - FilterPaneEditor: which fields the viewer's filter pane offers, on
 *    this page or on every page (definition.filterPane).
 *  - InteractionsEditor: how one visual reacts -- does clicking it filter
 *    the others, does it ignore dropdowns / chart clicks / some columns
 *    (widget.interaction, applied server-side in widget_data_service).
 *  - VisualFiltersEditor: fixed filters on one visual only (query.filters),
 *    e.g. "only Status = Open" on a single card. */
import { useQueries } from "@tanstack/react-query";
import { Filter, Plus, X } from "lucide-react";
import { useEffect, useState } from "react";

import { apiRequest } from "../api/client";
import { Button, Checkbox, Input, Select, Switch } from "../ds";
import { useDatasets } from "./LeftPanels";
import { tablesInReport } from "./ModelPanel";
import { uid, type SDef, type SWidget } from "./model";
import { TextFilterValuePicker } from "./TextFilterValuePicker";
import { BasicTextFilterPicker } from "./BasicTextFilterPicker";
import { basicTextFilter, type BasicTextSelection } from "./basicTextFilter";

interface FieldRow { source_name: string; display_name: string; data_type: string; field_role: string }
export interface PaneField { id: string; datasetId: string; field: string; label?: string }

function useTableFields(mode: "admin" | "owner", ids: string[]) {
  const base = mode === "admin" ? "/api/v1/admin/datasets" : "/api/v1/owner/datasets";
  const qs = useQueries({ queries: ids.map((id) => ({ queryKey: ["studio-fields", mode, id], queryFn: () => apiRequest<FieldRow[]>(`${base}/${id}/fields`), staleTime: 5 * 60_000 })) });
  return (id: string): FieldRow[] => (qs[ids.indexOf(id)]?.data as FieldRow[] | undefined) ?? [];
}

export function FilterPaneEditor({ mode, def, scope, pageId, onChange }: { mode: "admin" | "owner"; def: SDef; scope: "page" | "report"; pageId: string; onChange: (next: SDef, key?: string) => void }) {
  const datasets = useDatasets(mode);
  const tables = tablesInReport(def);
  const fieldsOf = useTableFields(mode, tables);
  const pane = (def.filterPane ?? {}) as { report?: PaneField[]; pages?: Record<string, PaneField[]> };
  const items: PaneField[] = scope === "report" ? pane.report ?? [] : pane.pages?.[pageId] ?? [];
  const [table, setTable] = useState("");
  const tableName = (id: string) => datasets.data?.find((d) => d.id === id)?.name ?? "Table";
  const write = (next: PaneField[]) => {
    const p = { report: pane.report ?? [], pages: { ...(pane.pages ?? {}) } };
    if (scope === "report") p.report = next;
    else if (next.length) p.pages[pageId] = next;
    else delete p.pages[pageId];
    onChange({ ...def, filterPane: p });
  };
  return (
    <div>
      <div className="ds-small ds-faint" style={{ marginBottom: 6 }}>
        {scope === "report" ? "Fields people can filter by on every page (Filters button on the viewer)." : "Extra fields people can filter by on this page only."}
      </div>
      {items.map((f) => (
        <div key={f.id} className="st-field">
          <Filter size={13} className="ds-faint" />
          <span className="ds-grow ds-truncate" title={tableName(f.datasetId)}>{f.label || fieldsOf(f.datasetId).find((x) => x.source_name === f.field)?.display_name || f.field}</span>
          <button type="button" className="st-mini" aria-label="Remove" onClick={() => write(items.filter((x) => x.id !== f.id))}><X size={12} /></button>
        </div>
      ))}
      {tables.length ? (
        <div className="ds-row" style={{ gap: 6, marginTop: 6 }}>
          <Select inputSize="sm" value={table} onChange={(e) => setTable(e.target.value)} aria-label="Table" style={{ flex: 1, minWidth: 0 }}>
            <option value="">Table…</option>
            {tables.map((t) => <option key={t} value={t}>{tableName(t)}</option>)}
          </Select>
          <Select inputSize="sm" value="" disabled={!table} aria-label="Add a field" style={{ flex: 1, minWidth: 0 }} onChange={(e) => {
            const col = fieldsOf(table).find((x) => x.source_name === e.target.value);
            if (col) write([...items, { id: uid("pf"), datasetId: table, field: col.source_name, label: col.display_name }]);
          }}>
            <option value="">+ Field…</option>
            {fieldsOf(table).filter((x) => x.field_role !== "measure" && !items.some((i) => i.datasetId === table && i.field === x.source_name)).map((x) => <option key={x.source_name} value={x.source_name}>{x.display_name}</option>)}
          </Select>
        </div>
      ) : <div className="ds-small ds-faint">Add a table to the report first (Data tab).</div>}
    </div>
  );
}

const CLICKABLE = new Set(["bar_chart", "line_chart", "area_chart", "combo_chart", "waterfall_chart", "donut_chart", "pie_chart", "treemap_chart", "funnel_chart"]);
const FILTER_TYPES = new Set(["filter", "date_filter", "text_filter"]);

/** Every field something in the report can filter by (dropdowns + filter pane). */
function filterFieldsOf(def: SDef): Array<{ field: string; label: string }> {
  const seen = new Map<string, string>();
  for (const p of def.pages ?? []) for (const w of p.widgets ?? []) {
    const f = w.query?.dimensions?.[0];
    if (FILTER_TYPES.has(w.type) && f) seen.set(f, w.title || f);
  }
  for (const g of def.globalFilters ?? []) { const f = g?.query?.dimensions?.[0]; if (f) seen.set(f, g.title || f); }
  const pane = (def.filterPane ?? {}) as { report?: PaneField[]; pages?: Record<string, PaneField[]> };
  for (const f of [...(pane.report ?? []), ...Object.values(pane.pages ?? {}).flat()]) seen.set(f.field, f.label || f.field);
  return [...seen.entries()].map(([field, label]) => ({ field, label }));
}

export function InteractionsEditor({ widget, def, onChange }: { widget: SWidget; def?: SDef; onChange: (patch: Partial<SWidget>) => void }) {
  const it = (widget.interaction ?? {}) as { clickToFilter?: boolean | null; ignoreFilters?: boolean; ignoreChartSelections?: boolean; ignoreFields?: string[] };
  const set = (patch: Record<string, unknown>) => onChange({ interaction: { ...it, ...patch } });
  const fields = def ? filterFieldsOf(def) : [];
  const ignored = new Set(it.ignoreFields ?? []);
  return (
    <div className="ds-stack" style={{ gap: 8 }}>
      {CLICKABLE.has(widget.type) ? (
        <Switch label="Clicking it filters the other visuals" checked={it.clickToFilter !== false} onChange={(v) => set({ clickToFilter: v ? null : false })} />
      ) : null}
      <Switch label="Ignore dropdowns and filters" checked={!!it.ignoreFilters} onChange={(v) => set({ ignoreFilters: v })} />
      <Switch label="Ignore clicks on other charts" checked={!!it.ignoreChartSelections} onChange={(v) => set({ ignoreChartSelections: v })} />
      {!it.ignoreFilters && fields.length ? (
        <div>
          <div className="ds-small ds-faint" style={{ margin: "2px 0 4px" }}>Or ignore only these filters</div>
          {fields.map((f) => (
            <Checkbox key={f.field} label={f.label} checked={ignored.has(f.field)} onChange={(v) => { const n = new Set(ignored); if (v) n.add(f.field); else n.delete(f.field); set({ ignoreFields: [...n] }); }} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

const OPS: Array<{ value: string; label: string; needsValue: boolean }> = [
  { value: "equals", label: "is", needsValue: true },
  { value: "not_equals", label: "is not", needsValue: true },
  { value: "in", label: "is one of", needsValue: true },
  { value: "not_in", label: "is not one of", needsValue: true },
  { value: "contains", label: "contains", needsValue: true },
  { value: "greater_than", label: "is more than", needsValue: true },
  { value: "less_than", label: "is less than", needsValue: true },
  { value: "is_not_null", label: "is not blank", needsValue: false },
  { value: "is_null", label: "is blank", needsValue: false },
];

export function VisualFiltersEditor({ widget, fields, onChange }: { widget: SWidget; fields: FieldRow[]; onChange: (patch: Partial<SWidget>) => void }) {
  const q = widget.query ?? {};
  const filters: Array<{ field: string; operator: string; value?: unknown }> = q.filters ?? [];
  const setFilters = (next: typeof filters) => onChange({ query: { ...q, filters: next } });
  const [draft, setDraft] = useState({ field: "", operator: "equals", value: "" });
  const [selected, setSelected] = useState<string[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [basicSelection, setBasicSelection] = useState<BasicTextSelection>({ all: true, values: [] });
  useEffect(() => { setEditingIndex(null); setSelected([]); setBasicSelection({ all: true, values: [] }); setDraft({ field: "", operator: "equals", value: "" }); }, [widget.id, widget.datasetId]);
  const col = fields.find((f) => f.source_name === draft.field);
  const isText = !!widget.datasetId && ["string", "text", "str"].includes(col?.data_type ?? "");
  const basic = isText && draft.operator === "basic";
  const textChoices = !!widget.datasetId && ["string", "text"].includes(col?.data_type ?? "") && ["equals", "not_equals", "in", "not_in"].includes(draft.operator);
  const listOperator = draft.operator === "in" || draft.operator === "not_in";
  const op = OPS.find((o) => o.value === draft.operator) ?? OPS[0];
  const label = (f: string) => fields.find((x) => x.source_name === f)?.display_name ?? f;
  const openFilter = (index: number) => {
    const filter = filters[index];
    const text = ["string", "text", "str"].includes(fields.find(f => f.source_name === filter.field)?.data_type ?? "");
    const list = Array.isArray(filter.value) ? filter.value.map(String) : [];
    const basicFilter = text && ["in", "not_in"].includes(filter.operator);
    setEditingIndex(index);
    setBasicSelection({ all: filter.operator === "not_in", values: list });
    setSelected(Array.isArray(filter.value) ? list : filter.value == null ? [] : [String(filter.value)]);
    setDraft({ field: filter.field, operator: basicFilter ? "basic" : filter.operator, value: Array.isArray(filter.value) ? list.join(", ") : filter.value == null ? "" : String(filter.value) });
  };
  const typed = (v: string, field: string) => {
    const col = fields.find((x) => x.source_name === field);
    return col?.data_type === "number" && v.trim() !== "" && !isNaN(Number(v)) ? Number(v) : v;
  };
  return (
    <div>
      {filters.map((f, i) => (
        <div key={i} className="st-field">
          <Filter size={13} className="ds-faint" />
          <span className="ds-grow ds-truncate" style={{ fontSize: 12.5 }}>{label(f.field)} {OPS.find((o) => o.value === f.operator)?.label ?? f.operator} {Array.isArray(f.value) ? f.value.join(", ") : f.value === undefined || f.value === null ? "" : String(f.value)}</span>
          <button type="button" className="st-mini" aria-label={`Edit ${label(f.field)} filter`} onClick={() => openFilter(i)}>Edit</button>
          <button type="button" className="st-mini" aria-label="Remove filter" onClick={() => { setFilters(filters.filter((_, j) => j !== i)); setEditingIndex(null); setDraft({ field: "", operator: "equals", value: "" }); }}><X size={12} /></button>
        </div>
      ))}
      <div className="ds-stack" style={{ gap: 4, marginTop: 6 }}>
        <Select inputSize="sm" value={draft.field} onChange={(e) => { const field = e.target.value; const existing = filters.findIndex(f => f.field === field && ["in", "not_in"].includes(f.operator)); if (existing !== -1) { openFilter(existing); return; } setEditingIndex(null); const text = ["string", "text", "str"].includes(fields.find(f => f.source_name === field)?.data_type ?? ""); setSelected([]); setBasicSelection({ all: true, values: [] }); setDraft({ field, operator: text && widget.datasetId ? "basic" : "equals", value: "" }); }} aria-label="Column">
          <option value="">Column…</option>
          {fields.map((f) => <option key={f.source_name} value={f.source_name}>{f.display_name}</option>)}
        </Select>
        <div className="ds-row" style={{ gap: 4 }}>
          <Select inputSize="sm" value={draft.operator} onChange={(e) => { const operator = e.target.value; if (!["in", "not_in"].includes(operator)) setSelected(selected.slice(0, 1)); setDraft({ ...draft, operator }); }} aria-label="Condition" style={{ flex: 1 }}>
            {isText ? <option value="basic">Basic filtering</option> : null}
            {OPS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
          {op.needsValue && !textChoices && !basic ? <Input inputSize="sm" value={draft.value} onChange={(e) => setDraft({ ...draft, value: e.target.value })} placeholder={listOperator ? "a, b, c" : "value"} aria-label="Value" style={{ flex: 1 }} /> : null}
        </div>
        {basic ? <BasicTextFilterPicker key={widget.id + draft.field} datasetId={widget.datasetId!} field={draft.field} selection={basicSelection} onChange={setBasicSelection} /> : null}
        {op.needsValue && textChoices ? <TextFilterValuePicker key={widget.id + draft.field} datasetId={widget.datasetId!} field={draft.field} multiple={listOperator} selected={selected} onChange={setSelected} /> : null}
        <Button size="sm" icon={Plus} disabled={!draft.field || (!basic && op.needsValue && (textChoices ? !selected.length : !draft.value.trim()))} onClick={() => {
          const value = !op.needsValue ? undefined : textChoices ? (listOperator ? selected : selected[0]) : listOperator ? draft.value.split(",").map((s) => typed(s.trim(), draft.field)).filter((s) => s !== "") : typed(draft.value.trim(), draft.field);
          const next = basic ? basicTextFilter(draft.field, basicSelection) : { field: draft.field, operator: draft.operator, ...(value === undefined ? {} : { value }) };
          setFilters(editingIndex === null ? [...filters, next] : filters.map((filter, index) => index === editingIndex ? { ...filter, ...next } : filter));
          setEditingIndex(null);
          setSelected([]);
          setDraft({ field: "", operator: "equals", value: "" });
        }}>{basic || editingIndex !== null ? "Apply filter" : "Add filter"}</Button>
      </div>
    </div>
  );
}
